import { Button, Input as DesignSystemInput, Textarea } from '@skin-seoul/design-system';
import {
  COLORS,

  FONT_SIZES,
  FONT_WEIGHTS,
  BORDER_RADIUS,
  SPACING,
  Z_INDEX,
  TRANSITIONS,
} from './constants';

export const Section = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div className="flex flex-col items-start" style={{ gap: SPACING.md, ...style }}>
    {children}
  </div>
);

export const Row = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-row" style={{ gap: SPACING.sm }}>
    {children}
  </div>
);

export const Divider = ({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) => (
  <div className="w-full h-px shrink-0" style={{ background: COLORS.border, ...style }}>
    {children}
  </div>
);

export const Label = ({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) => (
  <label
    htmlFor={htmlFor}
    style={{
      fontSize: FONT_SIZES.base,
      fontWeight: FONT_WEIGHTS.medium,
      color: COLORS.text,
      lineHeight: '140%',
    }}
  >
    {children}
  </label>
);

export const TextArea = ({
  id,
  maxLength,
  value,
  onChange,
}: {
  id?: string;
  maxLength?: number;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}) => (
  <Textarea
    id={id}
    maxLength={maxLength}
    value={value}
    onChange={onChange}
    className="outline-none scrollbar-hide"
    style={{
      fontSize: FONT_SIZES.md,
      border: `1px solid ${COLORS.border}`,
      padding: `${SPACING.md} ${SPACING.sm}`,
    }}
  />
);

export const FileInput = ({
  id,
  type,
  accept,
  multiple,
  onChange,
  disabled,
}: {
  id?: string;
  type: string;
  accept?: string;
  multiple?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}) => (
  <input
    id={id}
    type={type}
    accept={accept}
    multiple={multiple}
    onChange={onChange}
    disabled={disabled}
    style={{ display: 'none' }}
  />
);

export const StarRow = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-row" style={{ gap: SPACING.xs }}>
    {children}
  </div>
);

export const Star = ({
  children,
  selected: _selected,
  onClick,
  ...props
}: {
  children: React.ReactNode;
  selected: boolean;
  onClick: () => void;
  type?: 'button' | 'submit' | 'reset';
  'aria-label'?: string;
}) => (
  <button
    className="h-10 min-w-10 bg-transparent border-none p-0 cursor-pointer flex items-center justify-center [&_svg]:w-10 [&_svg]:h-10"
    onClick={onClick}
    {...props}
  >
    {children}
  </button>
);

export const ImageUploadBox = ({ children }: { children: React.ReactNode }) => (
  <div
    className="flex flex-row items-center flex-wrap"
    style={{ gap: SPACING.sm, width: '100%', marginTop: SPACING.xs }}
  >
    {children}
  </div>
);

export const Thumbnail = ({ children }: { children: React.ReactNode }) => (
  <div
    className="relative flex items-center justify-center overflow-visible"
    style={{ width: '100px', height: '100px', borderRadius: BORDER_RADIUS.sm, border: '1px solid #eee' }}
  >
    {children}
  </div>
);

export const ThumbnailImage = ({ src, alt }: { src: string; alt: string }) => (
  <img src={src} alt={alt} className="w-full h-full object-cover" style={{ borderRadius: BORDER_RADIUS.sm }} />
);

export const RemoveButton = ({
  children,
  onClick,
  ...props
}: {
  children: React.ReactNode;
  onClick: () => void;
  type?: 'button' | 'submit' | 'reset';
  'aria-label'?: string;
}) => (
  <button
    className="w-5 h-5 max-w-5 bg-generic-0 rounded-full border-none p-0 cursor-pointer absolute top-1 right-0.5 flex items-center justify-center z-[2]"
    onClick={onClick}
    {...props}
  >
    {children}
  </button>
);

export const UploadArea = ({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) => (
  <label
    htmlFor={htmlFor}
    style={{
      border: `1px dashed ${COLORS.borderDashed}`,
      borderRadius: BORDER_RADIUS.sm,
      width: '100%',
      height: '100px',
      background: COLORS.background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: TRANSITIONS.border,
    }}
    onMouseEnter={(e) => (e.currentTarget.style.borderColor = COLORS.borderHover)}
    onMouseLeave={(e) => (e.currentTarget.style.borderColor = COLORS.borderDashed)}
  >
    {children}
  </label>
);

export const SmallUploadArea = ({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) => (
  <label
    htmlFor={htmlFor}
    style={{
      border: `1px dashed ${COLORS.borderDashed}`,
      borderRadius: BORDER_RADIUS.sm,
      width: '100px',
      height: '100px',
      minWidth: '100px',
      minHeight: '100px',
      background: COLORS.background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: TRANSITIONS.border,
      marginTop: '0',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.borderColor = COLORS.borderHover)}
    onMouseLeave={(e) => (e.currentTarget.style.borderColor = COLORS.borderDashed)}
  >
    {children}
  </label>
);

export const RadioGroup = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col items-start" style={{ gap: SPACING.lg, marginTop: SPACING.sm }}>
    {children}
  </div>
);

export const RadioLabelWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col items-start" style={{ gap: SPACING.sm }}>
    {children}
  </div>
);

export const RadioLabel = ({ children }: { children: React.ReactNode }) => (
  <label
    className="flex items-center cursor-pointer"
    style={{
      gap: SPACING.sm,
      fontSize: FONT_SIZES.md,
      color: COLORS.text,
    }}
  >
    {children}
  </label>
);

export const Radio = ({
  type,
  name,
  value,
  checked,
  onChange,
}: {
  type: string;
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
}) => (
  <input
    type={type}
    name={name}
    value={value}
    checked={checked}
    onChange={onChange}
    style={{ accentColor: COLORS.primary, width: '18px', height: '18px' }}
  />
);

export const SubmitButton = ({
  children,
  disabled,
  onClick,
  type,
}: {
  children: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
  type?: 'button' | 'submit' | 'reset';
}) => (
  <Button
    variant="primary"
    className="w-full h-12 flex items-center justify-center"
    type={type}
    disabled={disabled}
    onClick={onClick}
    style={{ fontSize: FONT_SIZES.sm }}
  >
    {children}
  </Button>
);

export const CompleteButton = ({
  children,
  onClick,
  style,
}: {
  children: React.ReactNode;
  onClick: () => void;
  style?: React.CSSProperties;
}) => (
  <Button
    variant="primary"
    className="w-full cursor-pointer hover:opacity-90"
    onClick={onClick}
    style={{ fontSize: FONT_SIZES.base, ...style }}
  >
    {children}
  </Button>
);

export const GuideText = ({ children }: { children: React.ReactNode }) => (
  <p style={{ fontSize: FONT_SIZES.xs, color: COLORS.textSecondary, margin: 0 }}>
    {children}
  </p>
);

export const Optional = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{
      color: COLORS.borderDashed,
      fontSize: FONT_SIZES.xs,
      fontFeatureSettings: "'ss10' on",
      fontStyle: 'normal',
      lineHeight: '140%',
      fontWeight: FONT_WEIGHTS.normal,
    }}
  >
    {children}
  </span>
);

export const LabelWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-row items-center" style={{ gap: SPACING.sm }}>
    {children}
  </div>
);

export const BottomButtonRow = ({ children }: { children: React.ReactNode }) => <div>{children}</div>;

export const TermsBox = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col items-start" style={{ gap: SPACING.md }}>
    {children}
  </div>
);

export const TermsTitle = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{
      color: COLORS.primary,
      fontSize: FONT_SIZES.xs,
      fontFeatureSettings: "'ss10' on",
      fontStyle: 'normal',
      lineHeight: '140%',
      fontWeight: FONT_WEIGHTS.normal,
    }}
  >
    {children}
  </span>
);

export const TermsText = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{
      color: COLORS.textSecondary,
      fontSize: FONT_SIZES.xs,
      fontFeatureSettings: "'ss10' on",
      fontStyle: 'normal',
      lineHeight: '140%',
      fontWeight: FONT_WEIGHTS.normal,
    }}
  >
    {children}
  </span>
);

export const IntroSection = ({ children }: { children: React.ReactNode }) => (
  <div className="flex justify-between items-center min-h-[136px]" style={{ gap: SPACING.lg }}>
    {children}
  </div>
);

export const TextContainer = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col items-start flex-1 justify-center" style={{ gap: SPACING.sm }}>
    {children}
  </div>
);

export const IntroText = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{
      color: COLORS.text,
      fontSize: FONT_SIZES.sm,
      fontStyle: 'normal',
      lineHeight: '140%',
      fontWeight: FONT_WEIGHTS.normal,
    }}
  >
    {children}
  </span>
);

export const IntroSubText = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{
      color: COLORS.textSecondary,
      fontSize: FONT_SIZES.xs,
      fontFeatureSettings: "'ss10' on",
      fontStyle: 'normal',
      lineHeight: '140%',
      fontWeight: FONT_WEIGHTS.normal,
    }}
  >
    {children}
  </span>
);

export const ImageContainer = ({ children }: { children: React.ReactNode }) => (
  <div
    className="flex items-center justify-center shrink-0 overflow-hidden"
    style={{
      width: '136px',
      height: '136px',
      background: COLORS.backgroundSecondary,
      borderRadius: SPACING.lg,
    }}
  >
    {children}
  </div>
);

export const CompleteContainer = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div className="flex flex-col items-stretch" style={{ gap: SPACING.lg, ...style }}>
    {children}
  </div>
);

export const CompleteTitle = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <p
    style={{
      color: COLORS.text,
      fontWeight: FONT_WEIGHTS.semibold,
      lineHeight: '140%',
      ...style,
    }}
  >
    {children}
  </p>
);

export const CompleteDesc = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <p
    style={{
      color: COLORS.textSecondary,
      fontWeight: FONT_WEIGHTS.normal,
      lineHeight: '140%',
      ...style,
    }}
  >
    {children}
  </p>
);

export const ConfirmationOverlay = ({
  children,
  ...props
}: {
  children: React.ReactNode;
  initial?: { opacity: number };
  animate?: { opacity: number };
  exit?: { opacity: number };
  onClick?: () => void;
}) => (
  <div
    className="fixed top-0 left-0 right-0 bottom-0 flex items-center justify-center"
    style={{
      background: COLORS.backgroundOverlayDark,
      zIndex: Z_INDEX.confirmation,
    }}
    {...props}
  >
    {children}
  </div>
);

export const RequiredIndicator = ({ children }: { children: React.ReactNode }) => (
  <span style={{ color: COLORS.text }}>{children}</span>
);

export const Input = ({
  id,
  maxLength,
  value,
  onChange,
}: {
  id?: string;
  maxLength?: number;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
  <DesignSystemInput
    id={id}
    maxLength={maxLength}
    value={value}
    onChange={onChange}
    className="outline-none"
    style={{
      fontSize: FONT_SIZES.md,
      border: `1px solid ${COLORS.border}`,
      borderRadius: BORDER_RADIUS.sm,
      padding: `${SPACING.md} ${SPACING.sm}`,
    }}
  />
);
